import axios from 'axios';
import i18n from '../i18n';

const api = axios.create({ baseURL: '/api' });

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (email, password) =>
    api.post('/auth/login', new URLSearchParams({ username: email, password })),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const zonasAPI = {
  list: () => api.get('/zonas'),
  create: (data) => api.post('/zonas', data),
  update: (id, data) => api.put(`/zonas/${id}`, data),
  delete: (id) => api.delete(`/zonas/${id}`),
};

export const geocodeAPI = {
  search: (q) => api.get('/geocode', { params: { q } }),
};

export const colmenasAPI = {
  list: (params) => api.get('/colmenas', { params }),
  get: (id) => api.get(`/colmenas/${id}`),
  timeline: (id, params) => api.get(`/colmenas/${id}/timeline`, { params }),
  create: (data) => api.post('/colmenas', data),
  update: (id, data) => api.put(`/colmenas/${id}`, data),
  delete: (id) => api.delete(`/colmenas/${id}`),
  importCSV: (zonaId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/colmenas/import?zona_id=${zonaId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const estadoAPI = {
  get: (colmenaId) => api.get(`/colmenas/${colmenaId}/estado`),
  update: (colmenaId, key, valor) => api.patch(`/colmenas/${colmenaId}/estado/${key}`, { valor }),
};

export const tareasAPI = {
  list: (params = {}) => api.get('/tareas', { params }),
  create: (data) => api.post('/tareas', data),
  update: (id, data) => api.patch(`/tareas/${id}`, data),
  delete: (id) => api.delete(`/tareas/${id}`),
  completar: (id) => api.patch(`/tareas/${id}`, { estado: 'hecho' }),
  omitir: (id) => api.patch(`/tareas/${id}`, { estado: 'omitida' }),
};

export const eventosAPI = {
  list: (params) => api.get('/eventos', { params }),
  create: (data) => api.post('/eventos', data),
  update: (id, data) => api.put(`/eventos/${id}`, data),
  delete: (id) => api.delete(`/eventos/${id}`),
};

export const tagsAPI = {
  list: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
  delete: (id) => api.delete(`/tags/${id}`),
};

export const exportAPI = {
  csv: (tipo, lang) => api.get('/export/csv', { params: { ...(tipo ? { tipo } : {}), ...(lang ? { lang } : {}) }, responseType: 'blob' }),
  json: (tipo, lang) => api.get('/export/json', { params: { ...(tipo ? { tipo } : {}), ...(lang ? { lang } : {}) }, responseType: 'blob' }),
};

export const climaAPI = {
  get: (zonaId) => api.get(`/clima/${zonaId}`),
  sugerencias: (zonaId) => api.get('/sugerencias', { params: zonaId ? { zona_id: zonaId } : {} }),
};

export const varroaAPI = {
  evaluar: (data) => api.post('/varroa/evaluar', data),
  historico: (colmenaId, limit) => api.get(`/colmenas/${colmenaId}/varroa/historico`, { params: limit ? { limit } : {} }),
};

export const resumenAPI = {
  produccion: () => api.get('/resumen/produccion'),
};

export const avisosAPI = {
  carencia: (colmenaId, params) => api.get(`/colmenas/${colmenaId}/avisos-carencia`, { params }),
};

export const usersAPI = {
  updateLanguage: (language) => api.patch('/users/language', { language }),
  updatePerfilClimatico: (perfil_climatico) => api.patch('/users/perfil-climatico', { perfil_climatico }),
};

export const recordatoriosAPI = {
  list: () => api.get('/recordatorios'),
  update: (id, data) => api.patch(`/recordatorios/${id}`, data),
  testEmail: () => api.post('/recordatorios/test-email'),
};

export const alzasAPI = {
  list: (colmenaId) => api.get(`/colmenas/${colmenaId}/alzas`),
  activas: () => api.get('/alzas/activas'),
};

export const llmAPI = {
  analisis: (zonaId) => api.get('/llm/analisis', { params: { ...(zonaId ? { zona_id: zonaId } : {}), lang: i18n.language } }),
  consulta: (pregunta, zonaId) => api.post('/llm/consulta', { pregunta, zona_id: zonaId, lang: i18n.language }),
  historial: (limit = 20) => api.get('/llm/historial', { params: { limit } }),
  uso: () => api.get('/llm/uso'),
};

export const pushAPI = {
  vapidKey: () => api.get('/push/vapid-key'),
  subscribe: (sub) => api.post('/push/subscribe', sub),
  unsubscribe: () => api.post('/push/unsubscribe'),
};

export const fotoColmenaAPI = {
  upload: (colmenaId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/colmenas/${colmenaId}/foto`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (colmenaId) => api.delete(`/colmenas/${colmenaId}/foto`),
  imagenUrl: (colmenaId) => {
    const token = localStorage.getItem('token');
    return `/api/colmenas/${colmenaId}/foto/imagen?token=${token}`;
  },
};

export const fotosAPI = {
  list: (eventoId) => api.get(`/eventos/${eventoId}/fotos`),
  upload: (eventoId, files) => {
    const fd = new FormData();
    const fileList = Array.isArray(files) ? files : [files];
    fileList.forEach(f => fd.append('files', f));
    return api.post(`/eventos/${eventoId}/fotos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (fotoId) => api.delete(`/fotos/${fotoId}`),
  imagenUrl: (fotoId) => {
    const token = localStorage.getItem('token');
    return `/api/fotos/${fotoId}/imagen?token=${token}`;
  },
};

export const configAPI = {
  get: () => api.get('/config'),
};

export default api;
